import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import DayTab from '../components/DayTab';
import SessionCard from '../components/SessionCard';
import { TYPE_META } from '../components/TypeChip';
import Skeleton, { EmptyState } from '../components/Skeleton';
import { fetchAgenda, useLive } from '../data/live';
import { useNow, localHMS, isLive, isOver, todayDayIndex } from '../data/now';

function splitDayKey(key) {
  const [dowRaw, ...rest] = key.split(',');
  return { dow: dowRaw.trim(), date: rest.join(',').trim() };
}

// Legend order — panels first (the main event), housekeeping last.
const TRACK_ORDER = ['panel', 'keynote', 'insights', 'general', 'meal', 'social', 'golf', 'break'];

export default function AgendaPage() {
  const { meeting } = useOutletContext();
  const event = meeting._event;

  const agenda = useLive(fetchAgenda, [event?.id]);
  const dayKeys = agenda ? Object.keys(agenda) : [];
  const now = useNow();
  const todayIdx = todayDayIndex(agenda, now);
  // null = follow the clock (today during the meeting, else day 1) until the
  // attendee picks a day themselves.
  const [dayIdx, setDayIdx] = useState(null);
  const wanted = dayIdx ?? (todayIdx >= 0 ? todayIdx : 0);
  // Clamp if live data arrives with a different day count.
  const idx = Math.min(wanted, Math.max(0, dayKeys.length - 1));
  const currentKey = dayKeys[idx];
  const sessions = currentKey ? agenda[currentKey] : [];

  // On today's tab: mark live sessions and draw a "Now" line before the next
  // session to start (only once the day is under way).
  const isToday = idx === todayIdx;
  const hms = localHMS(now);
  const nextIdx = isToday ? sessions.findIndex((s) => s.start24 && s.start24 > hms) : -1;
  const dayStarted = isToday && sessions.some((s) => isLive(s, hms) || isOver(s, hms));
  const showNowLine = dayStarted && nextIdx > 0;

  // Scroll the "Now" marker (or the live session) into view once per visit.
  const nowRef = useRef(null);
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !isToday || !nowRef.current) return;
    scrolled.current = true;
    nowRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [isToday, currentKey]);

  if (agenda === undefined) {
    return (
      <div className="portal-agenda">
        <div className="portal-page-eyebrow">Agenda</div>
        <Skeleton variant="card" count={4} />
      </div>
    );
  }
  if (!dayKeys.length) {
    return (
      <div className="portal-agenda">
        <div className="portal-page-eyebrow">Agenda</div>
        <EmptyState>The agenda will be posted here as the meeting approaches.</EmptyState>
      </div>
    );
  }

  const panelCount = sessions.filter((s) => s.type === 'panel').length;
  const present = new Set(sessions.map((s) => s.type));
  const tracks = TRACK_ORDER.filter((t) => present.has(t)).map((t) => ({
    key: t,
    label: TYPE_META[t]?.label || t,
  }));

  return (
    <div className="portal-agenda">
      <div className="portal-agenda-head">
        <div className="portal-agenda-heading">
          <div className="portal-page-eyebrow">{isToday ? 'Agenda · Today' : 'Agenda'}</div>
          <h1 className="portal-page-h1">{currentKey || 'Agenda'}</h1>
        </div>
        <div className="portal-day-tabs">
          {dayKeys.map((key, i) => {
            const { dow, date } = splitDayKey(key);
            return (
              <DayTab
                key={key}
                dayOfWeek={dow}
                date={date}
                active={i === idx}
                onClick={() => setDayIdx(i)}
              />
            );
          })}
        </div>
      </div>

      <div className="portal-agenda-body">
        <div className="portal-agenda-list">
          {currentKey && (
            <div className="portal-day-sessions">
              {sessions.map((s, i) => {
                const live = isToday && isLive(s, hms);
                const firstLive = live && !sessions.slice(0, i).some((p) => isLive(p, hms));
                return (
                  <React.Fragment key={i}>
                    {showNowLine && i === nextIdx && (
                      <div
                        className="portal-now-line"
                        ref={sessions.some((p) => isLive(p, hms)) ? undefined : nowRef}
                      >
                        <span>Now · {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                      </div>
                    )}
                    <div ref={firstLive ? nowRef : undefined}>
                      <SessionCard
                        session={s}
                        live={live}
                        past={isToday && isOver(s, hms)}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <aside className="portal-agenda-rail">
          <div className="portal-agenda-card">
            <div className="portal-agenda-card-eyebrow">This day</div>
            <div className="portal-agenda-stat">
              <b>{sessions.length}</b> scheduled block{sessions.length === 1 ? '' : 's'}
            </div>
            {panelCount > 0 && (
              <div className="portal-agenda-stat">
                <b>{panelCount}</b> panel slot{panelCount === 1 ? '' : 's'}
              </div>
            )}
          </div>

          {tracks.length > 0 && (
            <div className="portal-agenda-card">
              <div className="portal-agenda-card-eyebrow">Tracks</div>
              {tracks.map((t) => (
                <div key={t.key} className="portal-agenda-legend-row">
                  <span className="portal-agenda-swatch" style={{ background: `var(--type-${t.key})` }} />
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
